// Core lib dependencies.
import Enums from '../config/enums.js';

/**
 * <pre>
 * Config service.
 *
 * Summary of responsibilities:
 *
 *  - provides abstraction layer for access to configuration data.
 * </pre>
 *
 * @author Various
 * @namespace Common.Services.ConfigService
 */
const ConfigService = (function () {
  'use strict';

  // Config.
  var config;

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Init.
   *
   * @memberOf Common.Services.ConfigService
   *
   * @param legacyConfig 		{object} Refefence to the legacy NGX app config object.
   * @param legacyContext 	{object} Refefence to the legacy NGX app context object.
   */
  const init = (legacyConfig, legacyContext) => {
    // For now, just grab the parts of the legacy config we need and map them across as desired...
    config = {
      // Campaign related.
      campaign: {
        id:          legacyConfig.id,
        type:        legacyConfig.type,
        appVersion:  legacyConfig.appVersion,
        isInNetwork: legacyConfig.isInNetwork,
      },

      // Key related.
      keys: {
        api:       legacyContext.apikey,
        recaptcha: legacyConfig.grecaptchaSiteKey,
        turnstile: legacyConfig.turnstileSiteKey,
      },

      // Endpoint related.
      endpoints: {
        vote:           legacyConfig.displayVoteURL,
        voteSecure:     legacyConfig.displayVoteURLsecure,
        metric:         legacyConfig.metricURL,
        metricSecure:   legacyConfig.metricURLsecure,
        contServ:       legacyConfig.displayContServiceV2URL,
        contServSecure: legacyConfig.displayContServiceV2URLsecure,
        counts:         legacyConfig.countsURL,
        countsSecure:   legacyConfig.countsURLsecure,
      },

      // Strategy related.
      strategies: {
        voting:      legacyConfig.votingStrategy,
        votingFraud: legacyConfig.votingFraudDetectionStrategy,
        judging:     legacyConfig.judgingStrategy,
      },

      // Asset related.
      asset: {
        url:    legacyContext.assetUrl,
        ugcUrl: legacyContext.ugcAssetPath,
      },

      // Social network related.
      social: {
        fb: {
          isFb: legacyContext.isFB,
          app:  {
            id: legacyContext.fb.app.id,
          },
          page: {
            id: legacyContext.fb.page.id,
          },
        },
      },

      // Voting related.
      voting: {
        requirePerm: legacyConfig.votingPermsRequired,
      },

      // User related.
      user: {
        uid: legacyContext.uid, // (this is NGX.context.uid)
      },

      // Staging related.
      stage: {
        mode:  legacyContext.isStage,
        token: legacyContext.stageToken,
      },

      // Preview related.
      preview: {
        isPreview: legacyContext.isPreview,
      },

      // Content services related.
      contentServices: {
        version: legacyConfig.contentServiceVersion,
      },

      // Auth related
      auth: {
        // disabled, social, or custom (social may be deprecated?)
        authConfig: legacyConfig.socialAuthConfig,
      },

      privacy: {
        isPrivacyMgmtEnabled:
          !!legacyContext.displayContainer &&
          legacyContext.displayContainer.privacyMgmt !== Enums.PRIVACY_MANAGEMENT_ENABLED.NONE,
        isPrivacyUIEnabled:
          !!legacyContext.displayContainer &&
          legacyContext.displayContainer.privacyMgmt === Enums.PRIVACY_MANAGEMENT_ENABLED.EXPERIENCES,
        isPreviewOrStaging: !legacyContext.displayContainer,
        cookieSettings:     {
          name:           'ngx_consent',
          expirationDays: 182, // 182 days = 6 months max cookie life
        },
        policyDate: new Date('2022-04-01'), // date of last policy change to force display when it changes
        policyURL:  'https://www.cheetahdigital.com/privacy-notice/',
      },
    };
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Mutator Methods.

  /**
   * Get a config key. Use dot notation to access nested properties.
   *
   * @memberOf Common.Services.ConfigService
   *
   * @param configKey {string} String containing dot notation of config property.
   * @return {mixed} The matching config property.
   */
  const get = (configKey) => {
    let i, len, def;

    // If no key was supplied, return everything.
    if (!configKey) {
      return config;
    }

    // Recursively reduce the config object untill we are left with the matching value.
    let matchedConfig = config;
    for (i = 0, configKey = configKey.split('.'), len = configKey.length; i < len; i++) {
      if (!matchedConfig || typeof matchedConfig !== 'object') {
        return def;
      }
      matchedConfig = matchedConfig[configKey[i]];
    }

    if (matchedConfig === undefined) return def;
    return matchedConfig;
  };

  // Expose scoped properties.
  return {
    init: init,
    get:  get,
  };
})();

export default ConfigService;
